import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { Avatar } from '../components/Avatar';
import { UiNode } from '../lib/UiNode';
import { IconClock, IconCalendar } from '../components/Icon';
import type { BoardDetail, MemberAvailabilityRow } from '../../shared/types';

export function SetupPhase({
  board,
  onBoardChange,
}: {
  board: BoardDetail;
  onBoardChange: (next: BoardDetail) => void;
}) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const isOwner = board.role === 'owner';

  return (
    <div className="flex flex-col gap-5">
      <section className="panel p-4">
        <h2 className="text-base">Who's in</h2>
        <ul className="mt-3 flex flex-col gap-2">
          {board.members.map((m) => (
            <li key={m.id} className="flex items-center gap-3">
              <Avatar user={m} size={28} />
              <span className="text-sm">{m.name}</span>
              <span className="text-xs text-cream-3">{m.email}</span>
              {m.role === 'owner' && <span className="chip">Owner</span>}
            </li>
          ))}
          {board.pendingInvites.map((i) => (
            <li key={i.email} className="flex items-center gap-3 text-cream-3">
              <span className="inline-block h-7 w-7 rounded-full border border-dashed border-cream-4" />
              <span className="text-sm">{i.email}</span>
              <span className="chip">Invited</span>
            </li>
          ))}
        </ul>

        {isOwner && (
          <form
            className="mt-4 flex flex-col gap-2 sm:flex-row"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!email.includes('@') || busy) return;
              setBusy(true);
              try {
                await api.invite(board.id, email.trim().toLowerCase());
                setEmail('');
                const next = await api.board(board.id);
                onBoardChange(next);
              } finally {
                setBusy(false);
              }
            }}
          >
            <UiNode
              as="input"
              name="setup-invite-input"
              entity="invitation"
              action="compose"
              className="input"
              type="email"
              placeholder="name@yourcompany.com"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            />
            <UiNode
              as="button"
              name="setup-invite-submit-button"
              entity="invitation"
              action="send"
              state={busy ? 'busy' : 'idle'}
              className="btn btn-primary"
              type="submit"
              disabled={busy}
            >
              {busy ? 'Adding…' : 'Add'}
            </UiNode>
          </form>
        )}
      </section>

      <section className="panel p-4">
        <h2 className="text-base">When</h2>
        <div className="mt-3 flex gap-1 rounded-lg border border-ink-rim/60 p-1 text-xs">
          {(['fixed', 'fuzzy', 'unset'] as const).map((m) => {
            const on = (board.dateMode ?? 'fixed') === m;
            return (
              <button
                key={m}
                type="button"
                disabled={!isOwner}
                className="flex-1 rounded-md px-2 py-1.5 capitalize"
                style={{
                  background: on ? 'var(--color-clove-3)' : 'transparent',
                  color: on ? 'var(--color-clove-2)' : 'var(--color-cream-3)',
                }}
                onClick={async () => {
                  await api.updateBoard(board.id, { dateMode: m });
                  onBoardChange({ ...board, dateMode: m });
                }}
              >
                {m === 'fixed' ? 'Fixed dates' : m === 'fuzzy' ? 'Rough idea' : 'Not yet'}
              </button>
            );
          })}
        </div>

        {(board.dateMode ?? 'fixed') === 'fixed' && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <DateField
              label="Starts"
              value={board.startsOn}
              disabled={!isOwner}
              onSave={async (v) => {
                await api.updateBoard(board.id, { startsOn: v });
                onBoardChange({ ...board, startsOn: v });
              }}
            />
            <DateField
              label="Ends"
              value={board.endsOn}
              disabled={!isOwner}
              onSave={async (v) => {
                await api.updateBoard(board.id, { endsOn: v });
                onBoardChange({ ...board, endsOn: v });
              }}
            />
          </div>
        )}

        {board.dateMode === 'fuzzy' && (
          <label className="mt-3 flex flex-col gap-1 text-xs text-cream-3">
            When-ish?
            <input
              className="input"
              type="text"
              placeholder='"a weekend in May", "second week of June", "Q3 sometime"'
              defaultValue={board.dateHint ?? ''}
              disabled={!isOwner}
              onBlur={async (e) => {
                const v = e.currentTarget.value.trim() || null;
                if (v !== board.dateHint) {
                  await api.updateBoard(board.id, { dateHint: v });
                  onBoardChange({ ...board, dateHint: v });
                }
              }}
            />
            <p className="text-[11px] text-cream-4">
              The AI will pick concrete dates when you generate the plan.
            </p>
          </label>
        )}

        {board.dateMode === 'unset' && (
          <p className="mt-3 text-xs text-cream-3">
            Skip dates for now. You can lock them in later before generating the plan.
          </p>
        )}
        <p className="mt-3 inline-flex items-center gap-1 text-xs text-cream-3">
          <IconClock size={12} />
          Default day window — when things happen:
        </p>
        <div className="mt-2 grid grid-cols-2 gap-3">
          <WorkingHourField
            label="Start"
            valueMin={board.workingStartMinute}
            disabled={!isOwner}
            onSave={async (m) => {
              await api.patchBoardCosmetic(board.id, { workingStartMinute: m });
              onBoardChange({ ...board, workingStartMinute: m });
            }}
          />
          <WorkingHourField
            label="End"
            valueMin={board.workingEndMinute}
            disabled={!isOwner}
            onSave={async (m) => {
              await api.patchBoardCosmetic(board.id, { workingEndMinute: m });
              onBoardChange({ ...board, workingEndMinute: m });
            }}
          />
        </div>
      </section>

      <MakeupSection board={board} isOwner={isOwner} onBoardChange={onBoardChange} />

      <AvailabilitySection board={board} />
    </div>
  );
}

/**
 * Group-makeup editor — kids / dietary / accessibility.
 *
 * Visible on Setup phase so the organiser tells the planner about
 * the humans BEFORE they generate the plan. Free-text fields, owner-
 * only edit. The planner treats these as MUST-honour constraints,
 * not nice-to-haves: a vegan in the group changes which restaurants
 * the plan proposes; a 4-year-old changes when dinner happens.
 *
 * Why textareas, not checkboxes/dropdowns: real constraints are
 * specific. "vegetarian" loses the nuance that one person is
 * pescatarian and another is allergic to mushrooms. Free-text lets
 * the organiser say what they actually need to say.
 */
function MakeupSection({
  board,
  isOwner,
  onBoardChange,
}: {
  board: BoardDetail;
  isOwner: boolean;
  onBoardChange: (b: BoardDetail) => void;
}) {
  return (
    <section className="panel flex flex-col gap-3 p-4">
      <header className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-medium">Who's in this group?</h2>
        <span className="text-[10px] uppercase tracking-wide text-cream-3">
          AI uses this
        </span>
      </header>
      <p className="text-xs text-cream-3">
        Tell the planner about kids, diets, or anyone needing extra care. Plans
        change around real humans — kid bedtimes, allergies, stairs.
      </p>
      <MakeupField
        label="Kids"
        placeholder="e.g. Charlotte is 4 (naps 1–3 pm), Theo is 7"
        initial={board.makeupKids ?? ''}
        disabled={!isOwner}
        onSave={async (v) => {
          await api.updateBoard(board.id, { makeupKids: v });
          onBoardChange({ ...board, makeupKids: v });
        }}
      />
      <MakeupField
        label="Dietary needs"
        placeholder="e.g. one vegetarian, severe peanut allergy, mum doesn't eat shellfish"
        initial={board.makeupDietary ?? ''}
        disabled={!isOwner}
        onSave={async (v) => {
          await api.updateBoard(board.id, { makeupDietary: v });
          onBoardChange({ ...board, makeupDietary: v });
        }}
      />
      <MakeupField
        label="Accessibility / pace"
        placeholder="e.g. grandma uses a walking stick — avoid hills + stairs, slower pace"
        initial={board.makeupAccessibility ?? ''}
        disabled={!isOwner}
        onSave={async (v) => {
          await api.updateBoard(board.id, { makeupAccessibility: v });
          onBoardChange({ ...board, makeupAccessibility: v });
        }}
      />
    </section>
  );
}

function MakeupField({
  label,
  placeholder,
  initial,
  disabled,
  onSave,
}: {
  label: string;
  placeholder: string;
  initial: string;
  disabled: boolean;
  onSave: (v: string | null) => Promise<void>;
}) {
  const [value, setValue] = useState(initial);
  const dirty = value.trim() !== initial.trim();
  return (
    <label className="flex flex-col gap-1.5 text-xs text-cream-3">
      {label}
      <textarea
        className="input min-h-[44px] text-sm"
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          if (!dirty) return;
          const trimmed = value.trim();
          void onSave(trimmed || null);
        }}
        maxLength={500}
      />
    </label>
  );
}

function AvailabilitySection({ board }: { board: BoardDetail }) {
  const [rows, setRows] = useState<MemberAvailabilityRow[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api
      .planState(board.id)
      .then((ps) => {
        if (!cancelled) setRows(ps.availability);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [board.id]);

  const dates = useMemo(() => enumerate(board.startsOn, board.endsOn), [board.startsOn, board.endsOn]);
  if (dates.length === 0) return null;

  function isUnavailable(userId: string, date: string): boolean {
    const r = rows.find((x) => x.userId === userId && x.date === date);
    return r ? !r.available : false;
  }

  async function toggle(userId: string, date: string): Promise<void> {
    if (busy) return;
    const wasUnavailable = isUnavailable(userId, date);
    const nextAvailable = wasUnavailable; // toggle
    const next = rows.filter((r) => !(r.userId === userId && r.date === date));
    next.push({ userId, date, available: nextAvailable, note: null });
    setRows(next);
    setBusy(true);
    try {
      await api.setAvailability(board.id, { userId, date, available: nextAvailable });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel p-4">
      <header className="mb-3 flex items-center gap-2">
        <IconCalendar size={14} />
        <h2 className="text-base">Availability</h2>
      </header>
      <p className="mb-3 text-xs text-cream-3">
        Click a cell to mark someone unavailable. The plan AI factors this in.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-cream-3">
              <th className="px-2 py-1 text-left">Member</th>
              {dates.map((d) => (
                <th key={d} className="whitespace-nowrap px-2 py-1 text-center font-normal">
                  {fmtShort(d)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {board.members.map((m) => (
              <tr key={m.id}>
                <td className="px-2 py-1">
                  <span className="inline-flex items-center gap-2">
                    <Avatar user={m} size={20} />
                    {m.name}
                  </span>
                </td>
                {dates.map((d) => {
                  const off = isUnavailable(m.id, d);
                  return (
                    <td key={d} className="px-1 py-1 text-center">
                      <button
                        type="button"
                        onClick={() => toggle(m.id, d)}
                        className="inline-block h-6 w-6 rounded-md border"
                        style={{
                          background: off ? 'var(--color-clove-3)' : 'transparent',
                          borderColor: off ? 'var(--color-clove)' : 'var(--color-ink-rim)',
                          color: off ? 'var(--color-clove)' : 'var(--color-cream-4)',
                        }}
                        title={off ? 'Unavailable — click to mark available' : 'Available — click to mark off'}
                      >
                        {off ? '✕' : ''}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function enumerate(a: string | null, b: string | null): string[] {
  if (!a || !b) return [];
  const out: string[] = [];
  const start = new Date(a + 'T00:00:00');
  const end = new Date(b + 'T00:00:00');
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return out;
  const cur = new Date(start);
  while (cur <= end) {
    // Build ISO from local components — `toISOString()` would convert
    // local midnight to UTC and shift the date backwards for any
    // timezone west of UTC (e.g. BST in May gives the 17th when the
    // user picked the 18th).
    out.push(
      `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`,
    );
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

function fmtShort(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
  });
}

function WorkingHourField({
  label,
  valueMin,
  disabled,
  onSave,
}: {
  label: string;
  valueMin: number;
  disabled: boolean;
  onSave: (m: number) => Promise<void>;
}) {
  const [v, setV] = useState(toTime(valueMin));
  return (
    <label className="flex flex-col gap-1 text-xs text-cream-3">
      {label}
      <input
        type="time"
        className="input"
        value={v}
        disabled={disabled}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => {
          const m = fromTime(v);
          if (m !== null && m !== valueMin) void onSave(m);
        }}
      />
    </label>
  );
}

function toTime(m: number): string {
  const h = Math.floor(m / 60).toString().padStart(2, '0');
  const mm = (m % 60).toString().padStart(2, '0');
  return `${h}:${mm}`;
}
function fromTime(s: string): number | null {
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

function DateField({
  label,
  value,
  disabled,
  onSave,
}: {
  label: string;
  value: string | null;
  disabled: boolean;
  onSave: (v: string | null) => Promise<void>;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-cream-3">
      {label}
      <input
        className="input"
        type="date"
        defaultValue={value ?? ''}
        disabled={disabled}
        onBlur={(e) => {
          const v = e.target.value || null;
          if (v !== value) void onSave(v);
        }}
      />
    </label>
  );
}
